/**
 * The small, shared pieces of the idea screens: verdict and disposition
 * pills, the industry label, the AI marker (score values and research status
 * pills live in ui/). Every one pairs colour with text or an icon —
 * colour is never the only signal.
 */

import {
  Archive,
  CheckCircle2,
  CircleAlert,
  CircleDot,
  CircleHelp,
  Hourglass,
  Loader2,
  OctagonX,
  PauseCircle,
  TriangleAlert,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

import type { Disposition, IdeaRow, Verdict, VerdictOutcome } from '@/api/types';
import { AiMarker } from '@/ui/AiMarker';

/** What an idea link carries: the page it was opened from, view and filters included. */
export interface IdeaLinkState {
  from?: string;
}

/**
 * Links to an idea that remember where they were opened, so the idea page's
 * back link returns to the same view, filters, sort and page (all in the URL).
 */
export function useIdeaLink() {
  const location = useLocation();
  const state: IdeaLinkState = { from: location.pathname + location.search };
  return (id: string) => ({ to: `/business-opportunities/${id}`, state });
}

export const VERDICT: Record<Verdict, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
  go: { label: 'Go', className: 'badge-success', Icon: CheckCircle2 },
  conditional_go: { label: 'Conditional go', className: 'badge-warning', Icon: CircleHelp },
  no_go: { label: 'No-go', className: 'badge-danger', Icon: OctagonX },
};

export function VerdictPill({ verdict, confidence }: { verdict: Verdict | null; confidence?: number | null }) {
  if (!verdict) return <span className="hint">No verdict</span>;
  const { label, className, Icon } = VERDICT[verdict];
  return (
    <span className={`badge ${className}`}>
      <Icon aria-hidden="true" />
      {label}
      {confidence !== undefined && confidence !== null && <span className="verdict-confidence">· {confidence}% confident</span>}
    </span>
  );
}

export const DISPOSITION_LABEL: Record<Disposition, string> = { active: 'Active', parked: 'Parked', archived: 'Archived' };

export const DISPOSITION_STYLE: Record<Disposition, { className: string; Icon: typeof CircleDot }> = {
  active: { className: 'badge-success', Icon: CircleDot },
  parked: { className: 'badge-warning', Icon: PauseCircle },
  archived: { className: 'badge-neutral', Icon: Archive },
};

/** Where an idea stands in triage: active, parked or archived. */
export function DispositionPill({ disposition }: { disposition: Disposition }) {
  const { className, Icon } = DISPOSITION_STYLE[disposition];
  return (
    <span className={`badge ${className}`}>
      <Icon aria-hidden="true" />
      {DISPOSITION_LABEL[disposition]}
    </span>
  );
}

/** The Verdict column's possible readings, in tile and filter order. */
export const OUTCOMES: VerdictOutcome[] = ['go', 'conditional_go', 'no_go', 'researching', 'failed'];

export const OUTCOME_STYLE: Record<VerdictOutcome, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
  ...VERDICT,
  researching: { label: 'In research', className: 'badge-info', Icon: Loader2 },
  failed: { label: 'Failed', className: 'badge-danger', Icon: TriangleAlert },
};

/** An industry research proposed and an admin has not reviewed yet. */
export function PendingReviewBadge() {
  return (
    <span className="badge badge-warning" title="Proposed by research; an admin approves or rejects it on Industries">
      <Hourglass aria-hidden="true" />
      Pending review
    </span>
  );
}

/**
 * Why an unassigned idea has no industry: research named one that exists but
 * is deactivated. One definition, used by every place that shows an idea's
 * industry — the matrix, the idea page's label and its admin control.
 */
export function IndustryHint({
  idea,
  compact = false,
}: {
  idea: Pick<IdeaRow, 'industry' | 'industry_hint'>;
  /** Just the icon, the text in its tooltip (the list's narrow Industry column). */
  compact?: boolean;
}) {
  if (idea.industry || !idea.industry_hint) return null;
  if (compact) {
    const text = `Suggested: ${idea.industry_hint.name} (inactive; reactivate it on Industries)`;
    return (
      <span className="industry-hint-icon" role="img" aria-label={text} title={text}>
        <CircleAlert aria-hidden="true" />
      </span>
    );
  }
  return (
    <span className="sub industry-hint">
      <CircleAlert aria-hidden="true" /> Suggested: {idea.industry_hint.name} (inactive; reactivate it on Industries)
    </span>
  );
}

/** The industry cell: the name with its AI and review markers, or Unassigned with any hint. */
export function IndustryLabel({
  idea,
  rationale,
  compact = false,
}: {
  idea: Pick<IdeaRow, 'industry' | 'industry_source' | 'industry_hint'>;
  rationale?: string | null;
  compact?: boolean;
}) {
  if (idea.industry) {
    return (
      <span className="industry-label">
        {idea.industry.name}
        {idea.industry_source === 'ai' && <AiMarker rationale={rationale} />}
        {idea.industry.status === 'pending' && <PendingReviewBadge />}
      </span>
    );
  }
  return (
    <span className="industry-label">
      <span className="hint">Unassigned</span>
      <IndustryHint idea={idea} compact={compact} />
    </span>
  );
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
